import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getImageUrl, publicAPI } from '../services/apiService';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';

const Portfolio = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const location = useLocation();
  
  // Check for category parameter in URL - default to real-estate (main focus)
  const urlParams = new URLSearchParams(location.search);
  const categoryParam = urlParams.get('category');
  const [activeFilter, setActiveFilter] = useState(categoryParam || 'immobilien');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Update filter when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      setActiveFilter(cat);
    }
  }, [location.search]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load projects from public JSON
      const projectsResponse = await fetch('/portfolio.json');
      const projectsData = await projectsResponse.json();
      setProjects(projectsData || []);
      
      // Load categories from API
      const categoriesResponse = await fetch(`${window.location.origin}/api/portfolio-categories`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        const activeCategories = categoriesData.categories?.filter(cat => cat.active) || [];
        
        // Sort by sort_order
        activeCategories.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        setCategories(activeCategories);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setProjects([]);
      // Fallback to default categories if API fails
      setCategories([
        { slug: 'all', name_de: 'Alle', name_ru: 'Все', id: 'all' },
        { slug: 'wedding', name_de: 'Hochzeiten', name_ru: 'Свадьбы', id: 'wedding' },
        { slug: 'portrait', name_de: 'Portraits', name_ru: 'Портреты', id: 'portrait' },
        { slug: 'event', name_de: 'Events', name_ru: 'События', id: 'event' },
        { slug: 'architecture', name_de: 'Architektur', name_ru: 'Архитектура', id: 'architecture' },
        { slug: 'restoration', name_de: 'Restauration', name_ru: 'Реставрация', id: 'restoration' },
        { slug: 'ai-photoshoot', name_de: 'KI-Fotosessions', name_ru: 'ИИ-Фотосессии', id: 'ai-photoshoot' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Map filter slugs to project categories (handles legacy data)
  const categoryMapping = {
    'immobilien': ['immobilien', 'real-estate'],
    'real-estate': ['immobilien', 'real-estate']
  };

  const filteredProjects = activeFilter === 'all' 
    ? (projects || [])
    : (projects || []).filter(project => {
        const mappedCategories = categoryMapping[activeFilter];
        if (mappedCategories) {
          return mappedCategories.includes(project.category);
        }
        return project.category === activeFilter;
      });

  const openLightbox = (images, initialIndex = 0) => {
    // Convert relative URLs to full URLs for lightbox
    const fullImages = images.map(img => getImageUrl(img));
    setLightboxImages(fullImages);
    setLightboxInitialIndex(initialIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxInitialIndex(0);
  };

  const seoData = {
    title: currentLang === 'ru' ? 'Портфолио - FotografX.de' : 'Portfolio - FotografX.de',
    description: currentLang === 'ru' ? 'Портфолио профессионального фотографа в Германии' : 'Portfolio des professionellen Fotografen in Deutschland',
    keywords: currentLang === 'ru' ? 'портфолио, фотография, свадьба, портрет' : 'Portfolio, Fotografie, Hochzeit, Portrait'
  };

  if (isLoading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      <SEO {...seoData} />
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1651344139792-4025fb1839d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxibGFjayUyMHdoaXRlJTIwcGhvdG9ncmFwaHl8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzU3NTA3MzU5fDA&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('portfolio.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 bg-gray-900 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.slug || category.id}
                onClick={() => setActiveFilter(category.slug || category.id)}
                className={`px-6 py-2 text-sm font-medium uppercase tracking-wide transition-all duration-300 ${
                  activeFilter === (category.slug || category.id)
                    ? 'bg-white text-black'
                    : 'bg-transparent text-gray-400 border border-gray-600 hover:text-white hover:border-white'
                }`}
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {currentLang === 'ru' ? category.name_ru : category.name_de}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
            {filteredProjects.map((project, index) => (
              <div 
                key={project.id} 
                className={`group cursor-pointer relative overflow-hidden bg-gray-900 ${
                  index % 3 === 0 ? 'md:col-span-2 lg:col-span-1' : ''
                } ${index % 5 === 0 ? 'lg:row-span-2' : ''}`}
                onClick={() => {
                  const projectImages = project.images && project.images.length > 0 
                    ? project.images 
                    : [project.image];
                  openLightbox(projectImages);
                }}
              >
                <div className="aspect-w-16 aspect-h-12 overflow-hidden">
                  <img 
                    src={getImageUrl(project.image)} 
                    alt={project[`title_${currentLang}`]}
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${
                      index % 5 === 0 ? 'h-96' : 'h-64'
                    }`}
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                  <div className="text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-6">
                    <h3 
                      className="text-xl font-semibold mb-2 group-hover:text-gray-300 transition-colors duration-300"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {currentLang === 'ru' ? project.title_ru : project.title_de}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {currentLang === 'ru' ? project.description_ru : project.description_de}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-gray-800 text-xs uppercase tracking-wider">
                        {project.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {project.images?.length || 1} {(project.images?.length || 1) === 1 ? 'Foto' : 'Fotos'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">
                {currentLang === 'de' 
                  ? 'Keine Projekte in dieser Kategorie gefunden.' 
                  : 'В этой категории проектов не найдено.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        initialIndex={lightboxInitialIndex}
      />
    </div>
  );
};

export default Portfolio;